import { RingBuffer } from '@/rtk/ringBufferApi';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import TanTable from '../Table/TanTable';
import { useTimeConversion } from '@/common/customHooks';
import { UI_DATE_FORMAT } from '@/common/constants';
import {
  redirectToAlarmPage,
  redirectToDataExplorerPage,
} from '@/common/domains';
import { useLocation, useNavigate } from 'react-router-dom';
import DotMenu from '@/common/components/DotMenu/DotMenu';
import { generateDotMenuOptions } from './utils';
import { dateSortingFn } from '../Table/tanTableUtils';

const columnHelper = createColumnHelper<RingBuffer>();

type Props = {
  data: RingBuffer[];
  isLoading: boolean;
  onClose: () => void;
};

const RingBufferTable = ({ data, isLoading, onClose }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const entityId = location.pathname.split('/')[2];

  const { toFarmLocalTime } = useTimeConversion();

  const getDotMenuOptions = useCallback(
    (item: RingBuffer) =>
      generateDotMenuOptions(
        entityId,
        item.fromDate,
        item.toDate,
        navigate,
        toFarmLocalTime,
        onClose,
      ),
    [
      entityId,
      redirectToDataExplorerPage,
      redirectToAlarmPage,
      navigate,
      toFarmLocalTime,
      onClose,
    ],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('requestedDate', {
        id: 'requestedDate',
        header: 'Requested timestamp',
        cell: ({ row }) => {
          const value = row.original.requestedDate;

          return value ? toFarmLocalTime(value, UI_DATE_FORMAT) : '-';
        },
        enableSorting: true,
        sortingFn: dateSortingFn,
        size: 160,
        meta: {
          testId: `table-column-requestedDate`,
        },
      }),

      columnHelper.accessor('requestType', {
        id: 'requestType',
        header: 'Request type',
        cell: ({ getValue }) => {
          const value = getValue();

          return value || '-';
        },
        enableSorting: true,
        size: 180,
        meta: {
          testId: `table-column-requestType`,
        },
      }),

      columnHelper.accessor('fromDate', {
        id: 'startDate',
        header: 'Start timestamp',
        cell: ({ row }) => {
          const value = row.original.fromDate;

          return value ? toFarmLocalTime(value, UI_DATE_FORMAT) : '-';
        },
        enableSorting: true,
        sortingFn: dateSortingFn,
        size: 140,
        meta: {
          testId: `table-column-startDate`,
        },
      }),

      columnHelper.accessor('toDate', {
        id: 'endDate',
        header: 'End timestamp',
        cell: ({ row }) => {
          const value = row.original.toDate;

          return value ? toFarmLocalTime(value, UI_DATE_FORMAT) : '-';
        },
        enableSorting: true,
        sortingFn: dateSortingFn,
        size: 140,
        meta: {
          testId: `table-column-endDate`,
        },
      }),

      columnHelper.accessor('userName', {
        id: 'userName',
        header: 'Requested by',
        cell: ({ getValue }) => {
          const value = getValue();

          return value || '-';
        },
        enableSorting: true,
        size: 200,
        meta: {
          testId: `table-column-userName`,
        },
      }),

      columnHelper.accessor('result', {
        id: 'result',
        header: 'Result',
        cell: ({ getValue }) => {
          const value = getValue();

          return value || '-';
        },
        enableSorting: true,
        size: 100,
        meta: {
          testId: `table-column-result`,
        },
      }),

      columnHelper.accessor(() => 'actions', {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const options = getDotMenuOptions(row.original);

          return <DotMenu options={options} title="Open period in:" />;
        },
        enableSorting: false,
        size: 30,
        meta: {
          testId: `table-column-actions`,
        },
      }),
    ],
    [toFarmLocalTime, getDotMenuOptions],
  );

  return (
    <TanTable
      data={data ?? []}
      columns={columns}
      isLoading={isLoading}
      className="scrollable-table ring-buffer-table"
      testId="ring-buffer-table"
      columnPinning={{ right: ['actions'] }}
      enableSorting
      defaultSortingKey="requestedDate"
      defaultSortingDirection="desc"
    />
  );
};

export default RingBufferTable;
